import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  EmptyState,
  PageHeader,
  Spinner,
} from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { listOrgSupplierPortfolio } from '../lib/api';

/**
 * /o/:orgSlug/suppliers — Org-tier AI Supplier Portfolio.
 *
 * V3-2-10: Full supplier portfolio list with KPI tiles, sortable
 * filterable table, bulk actions, and pagination. Design reference:
 * .claude/skills/ux-adversary/runs/v3-2-10-portfolio/round-2/portfolio-list.html
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ScoreBand = 'red' | 'amber' | 'green';
type RiskTier = 'critical' | 'high' | 'medium' | 'low';
type DisclosureStatus = 'disclosed' | 'pending' | 'declined' | 'none';

interface SupplierRow {
  portfolio_entry_id: string;
  supplier_display_name: string;
  risk_tier: RiskTier | null;
  score_band: ScoreBand | null;
  disclosure_status: DisclosureStatus | null;
  flags: string[];
  renewal_date: string | null;
  last_reviewed_at: string | null;
  workspace_slug?: string | null;
  workspace_name?: string | null;
}

interface PortfolioSummary {
  total: number;
  red: number;
  amber: number;
  green: number;
  red_delta: number;
  amber_delta: number;
  green_delta: number;
  last_synced_at: string | null;
}

type SortField = 'supplier_display_name' | 'risk_tier' | 'score_band' | 'disclosure_status' | 'renewal_date' | 'last_reviewed_at';
type SortDir = 'asc' | 'desc';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 25;

const SCORE_COLORS: Record<ScoreBand, string> = {
  red: 'var(--status-red)',
  amber: 'var(--status-amber)',
  green: 'var(--status-green)',
};

const DISCLOSURE_STYLES: Record<string, { bg: string; color: string }> = {
  disclosed: { bg: 'var(--status-green-bg)', color: 'var(--status-green)' },
  pending: { bg: 'var(--status-amber-bg)', color: 'var(--status-amber)' },
  declined: { bg: 'var(--status-red-bg)', color: 'var(--status-red)' },
};

const TIER_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const SCORE_ORDER: Record<string, number> = { red: 0, amber: 1, green: 2 };

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatDelta(n: number): string {
  if (n > 0) return `+${n} since last week`;
  if (n < 0) return `${n} since last week`;
  return 'No change';
}

function comparePrimitive(a: string | number | null, b: string | number | null, dir: SortDir): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a < b) return dir === 'asc' ? -1 : 1;
  if (a > b) return dir === 'asc' ? 1 : -1;
  return 0;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function OrgSupplierPortfolio() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();

  // Fetch
  const listQ = useQuery({
    queryKey: queryKeys.orgSuppliers(orgSlug ?? ''),
    queryFn: () => listOrgSupplierPortfolio(orgSlug!),
    enabled: !!orgSlug,
  });
  const list = {
    data: listQ.data ?? null,
    error: listQ.error?.message ?? null,
    loading: listQ.isLoading,
    refetch: listQ.refetch,
  };

  // Local UI state
  const [search, setSearch] = useState('');
  const [filterScore, setFilterScore] = useState<ScoreBand | ''>('');
  const [filterTier, setFilterTier] = useState<RiskTier | ''>('');
  const [filterDisclosure, setFilterDisclosure] = useState<DisclosureStatus | ''>('');
  const [sortField, setSortField] = useState<SortField>('score_band');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [kpiFilter, setKpiFilter] = useState<ScoreBand | null>(null);

  const rawData = list.data as { portfolio?: SupplierRow[] } | undefined;
  const rows: SupplierRow[] = useMemo(() => rawData?.portfolio ?? [], [rawData]);
  const summary: PortfolioSummary | null = useMemo(() => {
    if (!rows.length) return null;
    const red = rows.filter((r) => r.score_band === 'red').length;
    const amber = rows.filter((r) => r.score_band === 'amber').length;
    const green = rows.filter((r) => r.score_band === 'green').length;
    return { total: rows.length, red, amber, green, red_delta: 0, amber_delta: 0, green_delta: 0, last_synced_at: null };
  }, [rows]);

  // Derived: filter + sort
  const filtered = useMemo(() => {
    let result = rows;
    const lc = search.toLowerCase();
    if (lc) result = result.filter((r) => r.supplier_display_name.toLowerCase().includes(lc));
    const activeScore = kpiFilter || filterScore;
    if (activeScore) result = result.filter((r) => r.score_band === activeScore);
    if (filterTier) result = result.filter((r) => r.risk_tier === filterTier);
    if (filterDisclosure) result = result.filter((r) => r.disclosure_status === filterDisclosure);

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortField) {
        case 'supplier_display_name':
          return comparePrimitive(a.supplier_display_name, b.supplier_display_name, sortDir);
        case 'risk_tier':
          return comparePrimitive(TIER_ORDER[a.risk_tier ?? ''] ?? 99, TIER_ORDER[b.risk_tier ?? ''] ?? 99, sortDir);
        case 'score_band':
          return comparePrimitive(SCORE_ORDER[a.score_band ?? ''] ?? 99, SCORE_ORDER[b.score_band ?? ''] ?? 99, sortDir);
        case 'disclosure_status':
          return comparePrimitive(a.disclosure_status, b.disclosure_status, sortDir);
        case 'renewal_date':
          return comparePrimitive(a.renewal_date, b.renewal_date, sortDir);
        case 'last_reviewed_at':
          return comparePrimitive(a.last_reviewed_at, b.last_reviewed_at, sortDir);
        default:
          return 0;
      }
    });
    return result;
  }, [rows, search, filterScore, filterTier, filterDisclosure, sortField, sortDir, kpiFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Selection helpers
  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (selected.size === paged.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paged.map((r) => r.portfolio_entry_id)));
    }
  }, [paged, selected.size]);

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDir('asc');
      }
      return field;
    });
    setPage(0);
  }, []);

  const handleKpiToggle = useCallback((band: ScoreBand) => {
    setKpiFilter((prev) => (prev === band ? null : band));
    setPage(0);
  }, []);

  // Loading / error / empty
  if (list.loading) return <Spinner label="Loading supplier portfolio" />;
  if (list.error) return <Alert variant="error">{list.error}</Alert>;
  if (rows.length === 0) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <PageHeader title="AI Supplier Portfolio" subtitle="Third-party AI supplier risk across all workspaces." />
        <EmptyState
          title="No suppliers"
          description="Suppliers will appear here once workspaces add third-party AI vendors to their portfolio."
        />
      </div>
    );
  }

  const total = summary?.total ?? rows.length;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {/* Header */}
      <PageHeader
        title="AI Supplier Portfolio"
        subtitle={`${total} supplier${total === 1 ? '' : 's'}${summary?.last_synced_at ? ` · Last synced ${formatDate(summary.last_synced_at)}` : ''}`}
      />

      {/* KPI tiles */}
      {summary ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: 'var(--space-3)',
          }}
        >
          {/* Red hero tile */}
          <button
            type="button"
            onClick={() => handleKpiToggle('red')}
            aria-pressed={kpiFilter === 'red'}
            style={{
              background: 'var(--surface)',
              border: kpiFilter === 'red' ? '2px solid var(--accent)' : '2px solid var(--status-red)',
              borderRadius: 'var(--radius-card)',
              padding: 'var(--space-4)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              textAlign: 'left',
              transition: 'box-shadow 0.15s',
            }}
          >
            <div
              style={{
                background: 'var(--status-red-bg)',
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{ color: 'var(--status-red)', fontSize: 20, fontWeight: 700 }} aria-hidden="true">!</span>
            </div>
            <div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--status-red)' }}>{summary.red}</div>
              <div style={{ fontSize: 'var(--font-size-3xs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Red suppliers</div>
              <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 2, color: summary.red_delta > 0 ? 'var(--status-red)' : 'var(--text-muted)' }}>{formatDelta(summary.red_delta)}</div>
            </div>
          </button>

          {/* Amber tile */}
          <button
            type="button"
            onClick={() => handleKpiToggle('amber')}
            aria-pressed={kpiFilter === 'amber'}
            style={{
              background: 'var(--surface)',
              border: kpiFilter === 'amber' ? '2px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              padding: 'var(--space-3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              textAlign: 'left',
              transition: 'box-shadow 0.15s',
            }}
          >
            <div
              style={{
                background: 'var(--status-amber-bg)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{ color: 'var(--status-amber)', fontSize: 18, fontWeight: 700 }} aria-hidden="true">~</span>
            </div>
            <div>
              <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--status-amber)' }}>{summary.amber}</div>
              <div style={{ fontSize: 'var(--font-size-3xs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Amber suppliers</div>
              <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 2, color: 'var(--text-muted)' }}>{formatDelta(summary.amber_delta)}</div>
            </div>
          </button>

          {/* Green tile */}
          <button
            type="button"
            onClick={() => handleKpiToggle('green')}
            aria-pressed={kpiFilter === 'green'}
            style={{
              background: 'var(--surface)',
              border: kpiFilter === 'green' ? '2px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              padding: 'var(--space-3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              textAlign: 'left',
              transition: 'box-shadow 0.15s',
            }}
          >
            <div
              style={{
                background: 'var(--status-green-bg)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 8l3 3 5-6" stroke="var(--status-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--status-green)' }}>{summary.green}</div>
              <div style={{ fontSize: 'var(--font-size-3xs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Green suppliers</div>
              <div style={{ fontSize: 'var(--font-size-3xs)', marginTop: 2, color: 'var(--text-muted)' }}>{formatDelta(summary.green_delta)}</div>
            </div>
          </button>
        </div>
      ) : null}

      {/* Filters row */}
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-3)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <label htmlFor="search-suppliers" className="sr-only">Search suppliers</label>
          <input
            id="search-suppliers"
            type="search"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            style={{
              width: '100%',
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              borderRadius: 6,
              padding: '0.625rem 0.75rem',
              fontSize: 'var(--font-size-2xs)',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <label htmlFor="filter-score" className="sr-only">Filter by score</label>
          <select
            id="filter-score"
            value={filterScore}
            onChange={(e) => { setFilterScore(e.target.value as ScoreBand | ''); setKpiFilter(null); setPage(0); }}
            style={{
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              borderRadius: 6,
              padding: '0.625rem 0.75rem',
              fontSize: 'var(--font-size-2xs)',
            }}
          >
            <option value="">All scores</option>
            <option value="red">Red</option>
            <option value="amber">Amber</option>
            <option value="green">Green</option>
          </select>

          <label htmlFor="filter-tier" className="sr-only">Filter by risk tier</label>
          <select
            id="filter-tier"
            value={filterTier}
            onChange={(e) => { setFilterTier(e.target.value as RiskTier | ''); setPage(0); }}
            style={{
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              borderRadius: 6,
              padding: '0.625rem 0.75rem',
              fontSize: 'var(--font-size-2xs)',
            }}
          >
            <option value="">All tiers</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <label htmlFor="filter-disclosure" className="sr-only">Filter by disclosure</label>
          <select
            id="filter-disclosure"
            value={filterDisclosure}
            onChange={(e) => { setFilterDisclosure(e.target.value as DisclosureStatus | ''); setPage(0); }}
            style={{
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              borderRadius: 6,
              padding: '0.625rem 0.75rem',
              fontSize: 'var(--font-size-2xs)',
            }}
          >
            <option value="">All disclosures</option>
            <option value="disclosed">Disclosed</option>
            <option value="pending">Pending</option>
            <option value="declined">Declined</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 ? (
        <div
          role="toolbar"
          aria-label="Bulk actions"
          style={{
            background: 'var(--accent)',
            color: 'var(--surface)',
            padding: '0.625rem 1rem',
            borderRadius: 'var(--radius-card)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            fontSize: 'var(--font-size-2xs)',
            fontWeight: 500,
          }}
        >
          <span>{selected.size} supplier{selected.size === 1 ? '' : 's'} selected</span>
          <button
            type="button"
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'inherit', padding: '0.375rem 0.75rem', borderRadius: 4, cursor: 'pointer', fontSize: 'var(--font-size-2xs)', fontWeight: 500 }}
          >
            Request disclosure
          </button>
          <button
            type="button"
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'inherit', padding: '0.375rem 0.75rem', borderRadius: 4, cursor: 'pointer', fontSize: 'var(--font-size-2xs)', fontWeight: 500 }}
          >
            Export selected
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', opacity: 0.8, fontSize: 'var(--font-size-2xs)' }}
          >
            Clear
          </button>
        </div>
      ) : null}

      {/* Table */}
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-2xs)',
              color: 'var(--text)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--surface-elevated)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 'var(--space-3) var(--space-3)', width: 40 }}>
                  <label className="sr-only" htmlFor="select-all">Select all suppliers</label>
                  <input
                    type="checkbox"
                    id="select-all"
                    checked={paged.length > 0 && selected.size === paged.length}
                    onChange={toggleAll}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                </th>
                <SortHeader field="supplier_display_name" label="Supplier" current={sortField} dir={sortDir} onSort={handleSort} />
                <th style={{ textAlign: 'left', padding: 'var(--space-3) var(--space-3)', fontWeight: 500, color: 'var(--text-muted)' }}>Workspace</th>
                <SortHeader field="risk_tier" label="Risk tier" current={sortField} dir={sortDir} onSort={handleSort} />
                <SortHeader field="score_band" label="Score" current={sortField} dir={sortDir} onSort={handleSort} />
                <SortHeader field="disclosure_status" label="Disclosure" current={sortField} dir={sortDir} onSort={handleSort} />
                <th style={{ textAlign: 'left', padding: 'var(--space-3) var(--space-3)', fontWeight: 500, color: 'var(--text-muted)' }}>Flags</th>
                <SortHeader field="renewal_date" label="Renewal" current={sortField} dir={sortDir} onSort={handleSort} />
                <SortHeader field="last_reviewed_at" label="Last reviewed" current={sortField} dir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No suppliers match your filters.
                  </td>
                </tr>
              ) : (
                paged.map((row) => (
                  <tr
                    key={row.portfolio_entry_id}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-elevated)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                    onClick={() => {
                      // NAV-4-3: Prefer workspace-scoped supplier detail when the workspace is known.
                      if (row.workspace_slug) {
                        navigate(`/w/${encodeURIComponent(row.workspace_slug)}/app/suppliers/${encodeURIComponent(row.portfolio_entry_id)}?from=org&org=${encodeURIComponent(orgSlug ?? '')}`);
                      } else if (orgSlug) {
                        navigate(`/o/${encodeURIComponent(orgSlug)}/suppliers/${encodeURIComponent(row.portfolio_entry_id)}`);
                      }
                    }}
                  >
                    <td style={{ padding: 'var(--space-3)' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.portfolio_entry_id)}
                        onChange={() => toggleOne(row.portfolio_entry_id)}
                        aria-label={`Select ${row.supplier_display_name}`}
                        style={{ accentColor: 'var(--accent)' }}
                      />
                    </td>
                    <td style={{ padding: 'var(--space-3)' }}>
                      <Link
                        to={
                          row.workspace_slug
                            ? `/w/${encodeURIComponent(row.workspace_slug)}/app/suppliers/${encodeURIComponent(row.portfolio_entry_id)}?from=org&org=${encodeURIComponent(orgSlug ?? '')}`
                            : `/o/${orgSlug}/suppliers/${encodeURIComponent(row.portfolio_entry_id)}`
                        }
                        style={{ color: 'var(--accent)', fontWeight: 500 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.supplier_display_name}
                      </Link>
                    </td>
                    <td style={{ padding: 'var(--space-3)' }}>
                      {row.workspace_name ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0.125rem 0.5rem',
                            borderRadius: 4,
                            fontSize: 'var(--font-size-3xs)',
                            fontWeight: 500,
                            background: 'var(--surface-elevated)',
                            color: 'var(--text-muted)',
                            border: '1px solid var(--border)',
                          }}
                        >
                          {row.workspace_name}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-dim)' }}>--</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--space-3)' }}>
                      {row.risk_tier ? (
                        <Badge tone="neutral">{row.risk_tier}</Badge>
                      ) : null}
                    </td>
                    <td style={{ padding: 'var(--space-3)' }}>
                      {row.score_band ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: SCORE_COLORS[row.score_band],
                              display: 'inline-block',
                            }}
                            aria-hidden="true"
                          />
                          <span className="sr-only">{row.score_band}</span>
                          <span style={{ color: SCORE_COLORS[row.score_band], fontWeight: 500 }}>
                            {row.score_band.charAt(0).toUpperCase() + row.score_band.slice(1)}
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>--</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--space-3)' }}>
                      <DisclosureChip status={row.disclosure_status} />
                    </td>
                    <td style={{ padding: 'var(--space-3)' }}>
                      <FlagChips flags={row.flags} />
                    </td>
                    <td style={{ padding: 'var(--space-3)', color: 'var(--text-muted)' }}>
                      {formatDate(row.renewal_date)}
                    </td>
                    <td style={{ padding: 'var(--space-3)', color: 'var(--text-muted)' }}>
                      {formatDate(row.last_reviewed_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: 'var(--space-3) var(--space-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 'var(--font-size-2xs)',
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>
            Showing {filtered.length === 0 ? 0 : page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} suppliers
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              aria-label="Previous page"
              style={{
                background: 'var(--surface-sunken)',
                border: '1px solid var(--border)',
                color: page === 0 ? 'var(--text-dim)' : 'var(--text)',
                padding: '0.5rem 0.75rem',
                borderRadius: 6,
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                opacity: page === 0 ? 0.5 : 1,
                fontSize: 'var(--font-size-2xs)',
              }}
            >
              Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = totalPages <= 5 ? i : Math.min(Math.max(page - 2, 0), totalPages - 5) + i;
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setPage(pageNum)}
                  aria-current={pageNum === page ? 'page' : undefined}
                  style={{
                    background: pageNum === page ? 'var(--accent)' : 'var(--surface-sunken)',
                    border: pageNum === page ? 'none' : '1px solid var(--border)',
                    color: pageNum === page ? 'var(--surface)' : 'var(--text)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: pageNum === page ? 600 : 400,
                    fontSize: 'var(--font-size-2xs)',
                  }}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              aria-label="Next page"
              style={{
                background: 'var(--surface-sunken)',
                border: '1px solid var(--border)',
                color: page >= totalPages - 1 ? 'var(--text-dim)' : 'var(--text)',
                padding: '0.5rem 0.75rem',
                borderRadius: 6,
                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                opacity: page >= totalPages - 1 ? 0.5 : 1,
                fontSize: 'var(--font-size-2xs)',
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function SortHeader({
  field,
  label,
  current,
  dir,
  onSort,
}: {
  field: SortField;
  label: string;
  current: SortField;
  dir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = current === field;
  return (
    <th style={{ textAlign: 'left', padding: 'var(--space-3)', fontWeight: 500, color: 'var(--text-muted)' }}>
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: 'none',
          border: 'none',
          color: 'inherit',
          font: 'inherit',
          padding: 0,
        }}
      >
        {label}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          style={{ opacity: active ? 1 : 0.4, transition: 'opacity 0.15s' }}
        >
          {active && dir === 'desc' ? (
            <path d="M3 4l3 4 3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          ) : active && dir === 'asc' ? (
            <path d="M3 8l3-4 3 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          ) : (
            <path d="M3 5l3-3 3 3M3 7l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          )}
        </svg>
      </button>
    </th>
  );
}

function DisclosureChip({ status }: { status: DisclosureStatus | null }) {
  if (!status || status === 'none') {
    return <span style={{ color: 'var(--text-muted)' }}>None</span>;
  }
  const style = DISCLOSURE_STYLES[status];
  if (!style) return <span style={{ color: 'var(--text-muted)' }}>{status}</span>;
  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.125rem 0.5rem',
        borderRadius: 4,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 500,
      }}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function FlagChips({ flags }: { flags: string[] }) {
  if (!flags || flags.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {flags.map((flag) => (
        <span
          key={flag}
          style={{
            background: 'var(--status-amber-bg)',
            color: 'var(--status-amber)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '0.125rem 0.5rem',
            borderRadius: 4,
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 500,
          }}
          title={flag}
        >
          {flag === 'undisclosed_ai' ? (
            <>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M6 1l5 9H1l5-9z" fill="currentColor"/>
              </svg>
              Undisclosed AI
            </>
          ) : flag === 'drift' ? (
            <>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 8l4-5 4 5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Drift
            </>
          ) : (
            flag
          )}
        </span>
      ))}
    </span>
  );
}
